import cn from 'classnames'
import DigitalCreditCardFlyoutSvg from 'ext-ui/svg/illustration/DigitalCreditCardFlyoutSvg'
import moment from 'moment-timezone'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { CreateVirtualCardScreen } from 'src/app/CreateVirtualCardForm/CreateVirtualCardScreen'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import {
  CreditCardDropdownInfiniteScrollProps,
  CreditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import { CustomVirtualCardAddressFlatFields } from 'src/components/CustomVirtualCardAddressBox/CustomVirtualCardAddressBox'
import { PhysicalVirtualCardAddressFlatFields } from 'src/components/PhysicalVirtualCardBox/PhysicalVirtualCardBox'
import { RecurrenceFormValues } from 'src/components/RecurrenceForm/RecurrenceForm'
import {
  UserDropdownInfiniteScrollProps,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import VirtualCardForm, {
  VirtualCardFlatFormValues,
} from 'src/components/VirtualCardForm/VirtualCardForm'
import { OrganizationRoles, VirtualCardTypes } from 'src/enums'
import Trns, { useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import { UserAssociation } from 'src/models/user.models'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import { PostBulkVirtualCardRequest } from 'src/requests/postBulkVirtualCard.request'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { PostVirtualCardRequest } from 'src/requests/postVirtualCard.request'
import { toCents } from 'src/utils/currency.utils'
import { ErrorDetailMap } from 'src/utils/error.utils'

import style from './CreateVirtualCardIssuerCycleContent.style'

interface CreateVirtualCardIssuerCycleContentProps {
  userOptions: UserOption[]
  recentUserOptions?: UserOption[]
  isFormDisabled: boolean
  labelCreditCardsAsAccounts: boolean
  creditCardOptions: CreditCardOption[]
  receiptAttachments: ReceiptAttachmentModel[]
  uploadingAttachmentThumbnails: Record<string, string>
  postingVirtualCard: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  uploadAttachments: (attachments: PostReceiptAttachmentRequest[]) => void
  deleteAttachment: (attachmentId: string) => void
  postVirtualCardAndOpenToastAndCloseFlyout: (
    body: Partial<PostVirtualCardRequest>,
    inviteUsers?: boolean,
    role?: OrganizationRoles,
  ) => void
  postBulkVirtualCardAndOpenToastAndCloseFlyout: (
    body: Partial<PostBulkVirtualCardRequest>,
    inviteUsers?: boolean,
    role?: OrganizationRoles,
  ) => void
  setScreen: (val: CreateVirtualCardScreen) => void
  userInfiniteScrollProps: UserDropdownInfiniteScrollProps
  creditCardInfiniteScrollProps: CreditCardDropdownInfiniteScrollProps
  issuerCycleOptions: CreditCardListModel[]
  pageLocation: string
  showSoftAlert?: boolean
}

const CreateVirtualCardIssuerCycleContent: React.FC<
  CreateVirtualCardIssuerCycleContentProps
> = (props) => {
  const queryParams = useSelector(selectQueryParams)
  const { analytics } = useAnalyticsContext()
  const user = useSelector(selectUserListModelFromState)
  const creditCards = useSelector(selectCreditCardListModelsFromState)
  const translate = useTranslate()
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled

  // Track selected credit card to derive the issuerCycleConfig reset date
  const [selectedCreditCardId, setSelectedCreditCardId] = useState<
    string | undefined
  >(
    typeof queryParams.creditCardId === 'string'
      ? queryParams.creditCardId
      : props.issuerCycleOptions.length === 1
        ? props.issuerCycleOptions[0].id
        : undefined,
  )

  const selectedCreditCard: CreditCardListModel | undefined = useMemo(() => {
    if (selectedCreditCardId !== undefined) {
      // Prefer issuerCycleOptions (fresh React Query data with issuerCycleConfig)
      const fromOptions = props.issuerCycleOptions.find(
        (cc) => cc.id === selectedCreditCardId,
      )
      if (fromOptions !== undefined) return fromOptions
      // Fall back to Redux store
      return creditCards[selectedCreditCardId]
    }
    // Auto-select if only one option is available
    if (props.issuerCycleOptions.length === 1) {
      return props.issuerCycleOptions[0]
    }
    return undefined
  }, [selectedCreditCardId, creditCards, props.issuerCycleOptions])

  // TODO: Unlike UpdateVirtualCardIssuerCycleContent which uses moment.tz(nextAt, timezone),
  // this uses moment() without timezone — the Create form has no virtual card to get a
  // timezone from. For day-ordinal display (Do format) this is unlikely to cause issues
  // unless the cycle resets near midnight in a distant timezone.
  const resetsOnMoment = useMemo(() => {
    const nextAt = selectedCreditCard?.issuerCycleConfig?.nextAt
    if (nextAt == null) return undefined
    return moment(nextAt)
  }, [selectedCreditCard])

  const resetsOnDayOrdinal: string = useMemo(() => {
    if (resetsOnMoment == null) return '--'
    return resetsOnMoment.format('Do')
  }, [resetsOnMoment])

  const onSuccessfulSubmit = (
    flatFields: VirtualCardFlatFormValues,
    expenseDetails: Record<string, string>,
    _recurrence: RecurrenceFormValues,
    customAddressFields: CustomVirtualCardAddressFlatFields,
    physicalAddressFields: PhysicalVirtualCardAddressFlatFields,
    role?: OrganizationRoles,
  ): void => {
    // Integer-only validation (EX-38563): ensure no fractional cents
    if (
      flatFields.balanceDollars !== undefined &&
      Math.floor(flatFields.balanceDollars) !== flatFields.balanceDollars
    ) {
      // Return without submitting — VirtualCardForm's built-in validation
      // handles errors; fractional input is not valid for issuer cycle cards
      return
    }

    const fields = {
      creditCardId: flatFields.creditCardId,
      displayName: flatFields.displayName,
      expenseDetails: Object.entries(expenseDetails)
        .filter((keyVal) => !!keyVal[1])
        .map(([key, val]) => ({ categoryId: key, labelId: val })),
      balanceCents:
        flatFields.balanceDollars !== undefined
          ? toCents(flatFields.balanceDollars)
          : undefined,
      currency: flatFields.currency,
      notes: flatFields.notes,
      validTo:
        flatFields.validToMoment !== undefined
          ? flatFields.validToMoment.format('YYYY-MM-DD')
          : undefined,
      receiptAttachmentIds: props.receiptAttachments.map((att) => att.id),
      // IssuerCycle-specific: tells backend to create ISSUER_CYCLE type
      issuerCycle: true,
    }

    flatFields?.userEmails?.length === 1
      ? props.postVirtualCardAndOpenToastAndCloseFlyout(
          {
            ...fields,
            recipient: flatFields.userEmails[0],
            receiptRulesExempt: flatFields.receiptRulesExempt,
          },
          true,
          role,
        )
      : props.postBulkVirtualCardAndOpenToastAndCloseFlyout(
          {
            ...fields,
            recipients: flatFields.userEmails,
          },
          true,
          role,
        )

    analytics?.track('Successfully create virtual card', {
      type_of_card: VirtualCardTypes.issuerCycle,
      card_name: flatFields.displayName,
      credit_limit: flatFields.balanceDollars,
      active_until: flatFields.validToMoment?.format('YYYY-MM-DD'),
      expense_category: Object.keys(expenseDetails).length > 0,
      add_attachments:
        props.receiptAttachments && props.receiptAttachments.length > 0,
      add_notes: !!flatFields.notes,
      recipient_count: flatFields.userEmails?.length,
      is_recipient_me:
        flatFields.userEmails &&
        user &&
        flatFields.userEmails.includes(user.email),
      issuer:
        creditCards &&
        flatFields.creditCardId &&
        creditCards[flatFields.creditCardId]
          ? creditCards[flatFields.creditCardId].issuer
          : undefined,
    })
  }

  const initialFlatFields = {
    creditCardId:
      typeof queryParams.creditCardId === 'string'
        ? queryParams.creditCardId
        : props.issuerCycleOptions.length === 1
          ? props.issuerCycleOptions[0].id
          : undefined,
  }

  return (
    <div css={style} className={cn('CreateVirtualCardIssuerCycleContent')}>
      <ActionRightFlyoutContent
        title={<Trns source={'Create a Virtual Card'} />}
        specificElements={{
          subtitle: translate('Recurring Card'),
          imageComponent: <DigitalCreditCardFlyoutSvg />,
          changeLink: {
            ariaLabelSource: 'Change Card Type',
            changeMessage: <Trns source={'Change Card Type'} />,
            onChangeClick: (): void => {
              props.setScreen(CreateVirtualCardScreen.Pick)
            },
          },
        }}
      >
        <VirtualCardForm
          id={'create'}
          buttonText={<Trns source={'Create Card'} />}
          multiCardButtonText={<Trns source={'Create Cards'} />}
          onSuccessfulSubmit={onSuccessfulSubmit}
          onCreditCardChange={(creditCardId) => {
            setSelectedCreditCardId(creditCardId)
          }}
          resetsOnMomentProps={{
            value: resetsOnMoment,
            onChange: () => {},
            placeHolder: '--',
          }}
          loading={props.postingVirtualCard}
          userOptions={props.userOptions}
          recentUserOptions={props.recentUserOptions}
          userFieldLabel={<Trns source={'Recipients'} />}
          customAddressEditable
          physicalCardEditable
          recipientNamesEditable={false}
          recurrenceEditable={false}
          notesEditable
          creditCardEditable
          amountProps={{
            amountEditable: true,
            amountPlaceholder: translate('Monthly Credit Limit'),
          }}
          initialFlatFields={initialFlatFields}
          creditCardProps={{
            isFormDisabled: props.isFormDisabled,
            creditCardOptions: props.creditCardOptions,
            labelCreditCardsAsAccounts: props.labelCreditCardsAsAccounts,
          }}
          attachmentsProps={{
            receiptAttachments: props.receiptAttachments,
            uploadingAttachmentThumbnails: props.uploadingAttachmentThumbnails,
            uploadAttachments: props.uploadAttachments,
            deleteAttachment: props.deleteAttachment,
          }}
          topLevelError={props.topLevelError}
          errorDetailMap={props.errorDetailMap}
          userInfiniteScrollProps={props.userInfiniteScrollProps}
          creditCardInfiniteScrollProps={props.creditCardInfiniteScrollProps}
          isMulti
          suggestedProps={{
            showSuggested: true,
            showOwnUserInSuggested: true,
          }}
          filterUserbyAssocationTypes={[UserAssociation.virtualCardTo]}
          unableToAddNewUser={
            showGuestRole ? false : !user?.hasOrgAdminPermissions
          }
          typeofCard={VirtualCardTypes.issuerCycle}
          showCardApprovalSoftAlert={props.showSoftAlert}
          resetsOnHelperText={
            <span>
              {translate(
                'Your credit limit resets to this amount on the ${date}.',
                { date: resetsOnDayOrdinal },
              )}
            </span>
          }
        />
      </ActionRightFlyoutContent>
    </div>
  )
}

export default CreateVirtualCardIssuerCycleContent
